import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Container,
  Paper,
  Alert
} from '@mui/material';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../index';
import { Player, TeamColor } from '../types';

export const PlayerRegistration: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [tableNumber, setTableNumber] = useState<number>(1);
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!sessionId) {
      setError('セッションIDが見つかりません');
      return;
    }

    // バリデーション
    if (displayName.length < 1 || displayName.length > 20) {
      setError('表示名は1〜20文字で入力してください');
      return;
    }

    setLoading(true);
    try {
      // 名前とテーブル番号からハッシュ値を生成してプレイヤーIDとする
      const hashInput = `${sessionId}_${displayName}_${tableNumber}`;
      const encoder = new TextEncoder();
      const data = encoder.encode(hashInput);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      const playerId = `player_${hashHex.substring(0, 16)}`; // 16文字に短縮
      
      // 既存のプレイヤーかチェック
      const existingPlayer = await getDoc(doc(db, 'battleRoyale', sessionId, 'players', playerId));
      
      if (existingPlayer.exists()) {
        // 既存のプレイヤーの場合、セッションストレージに保存
        sessionStorage.setItem('battleRoyalePlayerId', playerId);
        sessionStorage.setItem('battleRoyaleSessionId', sessionId);
        
        const playerData = existingPlayer.data() as Player;
        // チュートリアル完了済み（ownAnswerIdが設定されている）場合はゲーム画面へ
        if (playerData.ownAnswerId) {
          navigate(`/battle-royale/player/${sessionId}/game`);
          return;
        }
      } else {
        // 新規プレイヤーの場合、データを作成
        const playerData: Omit<Player, 'id'> = {
          displayName,
          tableNumber,
          teamColor: TeamColor.RED, // 仮のチーム（OP謎で決定）
          ownAnswerId: '',
          answeredIds: [], // 空の配列で初期化
          score: 0,
          createdAt: new Date()
        };

        await setDoc(
          doc(db, 'battleRoyale', sessionId, 'players', playerId),
          playerData
        );

        // セッションストレージに保存
        sessionStorage.setItem('battleRoyalePlayerId', playerId);
        sessionStorage.setItem('battleRoyaleSessionId', sessionId);
      }

      // OP謎回答画面へ遷移
      navigate(`/battle-royale/player/${sessionId}/tutorial`);
    } catch (err) {
      console.error('Registration error:', err);
      setError('登録に失敗しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography variant="h5" component="h1" gutterBottom align="center">
            バトロワなぞとき交渉会
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }} align="center">
            参加登録
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>テーブル番号</InputLabel>
              <Select
                value={tableNumber}
                onChange={(e) => setTableNumber(Number(e.target.value))}
                label="テーブル番号"
              >
                {[...Array(8)].map((_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {i + 1}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="表示名（ニックネーム）"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="なぞとき太郎"
              inputProps={{ maxLength: 20 }}
              helperText={`${displayName.length}/20文字`}
              sx={{ mb: 4 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !displayName.trim()}
              sx={{ py: 2 }}
            >
              次へ進む
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};